import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useAnalyticsEvents from '../useAnalyticsEvents';
import { useEvent } from '@/store';
import useTrackingDecorators from './decorators';
import { compose } from '@/utils/compose';

/**
 * Brings base tracking functionality
 * @returns {Object} Compound tracking funtions
 */

export default function useTrackingCore() {
  const { sendEventToPosthog } = useAnalyticsEvents();
  const {
    withCoupon,
    withCurrency,
    withPaymentConfig,
    withTotalPrice,
    withValue,
    withTimeRemaining,
    withTotalTickets,
    withUserPhone,
  } = useTrackingDecorators();

  const booking = useSelector((state) => state.booking);

  const {
    event: {
      event: { id: eventId, name, use_seats: useSeats },
    },
  } = useEvent();

  const coreDecorators = useMemo(() => {
    return {
      trackEventWithoutProperties: (eventName) => {
        sendEventToPosthog(eventName);
      },

      trackEvent: (eventName, data, isForWallet = false) => {
        const dataToSend = {
          event_id: eventId,
          event_name: name,
          event_type: useSeats ? 'con_mapa' : 'sin_mapa',
          booking: booking?.order || null,
          ...data,
        };

        if (isForWallet) {
          return dataToSend;
        }

        sendEventToPosthog(eventName, dataToSend);
      },
      trackEventWithCoupon: (eventName, data) =>
        withCoupon((eventName, data) =>
          coreDecorators.trackEvent(eventName, data),
        )(eventName, data),
      trackEventWithCurrency: (eventName, data) =>
        withCurrency((eventName, data) =>
          coreDecorators.trackEvent(eventName, data),
        )(eventName, data),
      trackEventWithPaymentConfig: (eventName, data) =>
        withPaymentConfig((eventName, data) =>
          coreDecorators.trackEvent(eventName, data),
        )(eventName, data),
      trackEventWithTotalPrice: (eventName, data) =>
        withTotalPrice((eventName, data) =>
          coreDecorators.trackEvent(eventName, data),
        )(eventName, data),
      trackEventWithTimeRemaining: (eventName, data) =>
        withTimeRemaining((eventName, data) =>
          coreDecorators.trackEvent(eventName, data),
        )(eventName, data),
      // compound tracking functions
      trackEventWithCouponValueAndTimeRemaining: (eventName, data) => {
        compose(
          withValue,
          withCoupon,
          withTimeRemaining,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        );
      },
      trackEventWithCouponAndCurrency: (eventName, data) =>
        compose(
          withCurrency,
          withCoupon,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        ),
      trackEventWithCouponCurrencyAndPrice: (eventName, data) =>
        compose(
          withTotalPrice,
          withCurrency,
          withCoupon,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        ),

      trackEventWithCouponAndPaymentConfig: (eventName, data) =>
        compose(
          withPaymentConfig,
          withCoupon,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        ),

      trackEventWithCouponAndTimeRemaining: (eventName, data) =>
        compose(
          withTimeRemaining,
          withCoupon,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        ),

      trackEventWithCouponTotalTicketsAndTimeRemaining: (eventName, data) =>
        compose(
          withTimeRemaining,
          withTotalTickets,
          withCoupon,
        )((eventName, data) => coreDecorators.trackEvent(eventName, data))(
          eventName,
          data,
        ),
      trackLeavePageEvent: (eventName, data, isForWallet = false) =>
        compose(
          withTotalTickets,
          withTotalPrice,
          withTimeRemaining,
          withCoupon,
          withUserPhone,
        )((eventName, data, isForWallet) =>
          coreDecorators.trackEvent(eventName, data, isForWallet),
        )(eventName, data, isForWallet),
    };
  }, [
    withCoupon,
    withCurrency,
    withPaymentConfig,
    withTotalPrice,
    withTimeRemaining,
    withTotalTickets,
    withUserPhone,
    sendEventToPosthog,
    eventId,
    name,
    useSeats,
  ]);

  return coreDecorators;
}
